import React, { useState } from 'react';
import { Modal, Steps, Form, Input, Select, Button, Row, Col, Typography, Divider, Checkbox, InputNumber, message, Card, Upload } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  PlusOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  PictureOutlined,
  CheckCircleFilled,
  TrophyOutlined,
  BookOutlined,
  TeamOutlined,
  SafetyOutlined,
  UserOutlined,
  UploadOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { beneficiaryAPI } from '../services/api';
import { addNotification } from '../services/notifications';
import ImageUpload from './ImageUpload';
import { BENEFICIARY_CATEGORIES } from '../constants/beneficiaryCategories';
import './InviteBeneficiaryModal.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface InviteBeneficiaryModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

const InviteBeneficiaryModal: React.FC<InviteBeneficiaryModalProps> = ({
  visible,
  onCancel,
  onSubmit
}) => {
  const [form] = Form.useForm();
  const [basicDetails, setBasicDetails] = useState<any>({});
  const [trustTransparency, setTrustTransparency] = useState<any>({});
  const [uploadImages, setUploadImages] = useState<any>({});
  const [saving, setSaving] = useState(false);
  // Image URLs from Supabase Storage uploads
  const [mainImageUrl, setMainImageUrl] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [additionalImages, setAdditionalImages] = useState<string[]>([]);
  // Form 990 file
  const [form990File, setForm990File] = useState<any>(null);
  // Profile links
  const [profileLinks, setProfileLinks] = useState<Array<{ channel: string; username: string }>>([{ channel: '', username: '' }]);

  const steps = [
    {
      title: 'Basic Information',
      icon: <FileTextOutlined style={{ color: '#324E58' }} />,
      description: basicDetails.beneficiaryName || basicDetails.city ? `${basicDetails.beneficiaryName || ''} ${basicDetails.city ? `(${basicDetails.city}, ${basicDetails.state || ''})` : ''}` : 'Organization name here'
    },
    {
      title: 'Trust & Transparency',
      icon: <SafetyOutlined style={{ color: '#324E58' }} />,
      description: trustTransparency.ein || 'Trust info here'
    },
    {
      title: 'Upload Images',
      icon: <PictureOutlined style={{ color: '#324E58' }} />,
      description: uploadImages.imageCount || 'Number of images here'
    }
  ];

  // Image upload handlers - using Supabase Storage
  const handleMainImageChange = (url: string | null) => {
    setMainImageUrl(url);
    form.setFieldsValue({ mainImage: url });
  };

  const handleLogoChange = (url: string | null) => {
    setLogoUrl(url);
    form.setFieldsValue({ logo: url });
  };

  const handleAdditionalImageChange = (url: string | null, index: number) => {
    if (url) {
      const newImages = [...additionalImages];
      newImages[index] = url;
      setAdditionalImages(newImages);
    } else {
      const newImages = additionalImages.filter((_, i) => i !== index);
      setAdditionalImages(newImages);
    }
  };

  // Add profile link
  const addProfileLink = () => {
    setProfileLinks([...profileLinks, { channel: '', username: '' }]);
  };

  // Remove profile link
  const removeProfileLink = (index: number) => {
    const newLinks = profileLinks.filter((_, i) => i !== index);
    setProfileLinks(newLinks);
  };

  // Update profile link
  const updateProfileLink = (index: number, field: 'channel' | 'username', value: string) => {
    const newLinks = [...profileLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setProfileLinks(newLinks);
    form.setFieldsValue({ profileLinks: newLinks });
  };

  const handleSubmit = async (allData: any) => {
    setSaving(true);
    
    try {
      const fetchAllBeneficiaries = async () => {
        const collected: any[] = [];
        let page = 1;
        const limit = 200;
        let total = 0;
        let response: any = null;

        do {
          response = await beneficiaryAPI.getBeneficiaries(page, limit);
          if (response?.success && Array.isArray(response.data)) {
            collected.push(...response.data);
          }
          total = response?.pagination?.total || collected.length;
          page += 1;
        } while (collected.length < total);

        return collected;
      };

      const existingBeneficiaries = await fetchAllBeneficiaries();
      const normalizedName = (allData.beneficiaryName || '').toString().trim().toLowerCase();
      const normalizedEmail = (allData.primaryEmail || '').toString().trim().toLowerCase();
      const duplicateBeneficiary = existingBeneficiaries.find((beneficiary: any) => {
        const existingName = (beneficiary.name || beneficiary.beneficiaryName || '').toString().trim().toLowerCase();
        const existingEmail = (beneficiary.email || beneficiary.primary_email || beneficiary.primaryEmail || '').toString().trim().toLowerCase();
        return (normalizedEmail && existingEmail && existingEmail === normalizedEmail) ||
          (normalizedName && existingName && existingName === normalizedName);
      });

      if (duplicateBeneficiary) {
        message.error('A beneficiary with this email or name already exists.');
        setSaving(false);
        return;
      }

      // Transform data to API format
      // CRITICAL: Only include fields that definitely exist in the backend schema
      // We use a conservative approach to avoid 400 errors
      
      // Build minimal safe payload with only core required fields
      // CRITICAL: Backend requires name field - send all three variations for compatibility
      // The name should already be validated and set in allData.beneficiaryName above
      const charityName = allData.beneficiaryName?.trim() || '';
      
      
      // This should never happen since we validate above, but double-check
      if (!charityName) {
        console.error('❌ CRITICAL: Charity name is still missing after validation!', {
          allData,
          basicDetails,
          trustTransparency
        });
        message.error('Charity name is required — please enter the organization name.');
        setSaving(false);
        return;
      }
      
      const beneficiaryData: any = {
        // Core required fields (definitely exist)
        // CRITICAL: Send name in all three formats backend accepts to ensure compatibility
        name: charityName,
        beneficiaryName: charityName,
        charityName: charityName,
        category: allData.category || '',
        type: allData.type || 'Medium', // Large, Medium, or Small
        about: allData.about || '',
        // Impact & Story fields - CRITICAL: Ensure these are included
        // Impact & Story fields - send in both formats for backend compatibility
        why_this_matters: allData.whyThisMatters || allData.why_this_matters || '',
        whyThisMatters: allData.whyThisMatters || allData.why_this_matters || '', // Also send camelCase
        success_story: allData.successStory || allData.success_story || '',
        successStory: allData.successStory || allData.success_story || '', // Also send camelCase
        story_author: allData.storyAuthor || allData.story_author || '',
        storyAuthor: allData.storyAuthor || allData.story_author || '', // Also send camelCase
        // Impact Statements - send in both formats for backend compatibility
        // Only send if they have non-empty values (send null instead of empty string)
        impact_statement_1: (allData.impactStatement1 && allData.impactStatement1.trim()) || 
                            (allData.impact_statement_1 && allData.impact_statement_1.trim()) || 
                            null,
        impactStatement1: (allData.impactStatement1 && allData.impactStatement1.trim()) || 
                          (allData.impact_statement_1 && allData.impact_statement_1.trim()) || 
                          null, // Also send camelCase
        impact_statement_2: (allData.impactStatement2 && allData.impactStatement2.trim()) || 
                            (allData.impact_statement_2 && allData.impact_statement_2.trim()) || 
                            null,
        impactStatement2: (allData.impactStatement2 && allData.impactStatement2.trim()) || 
                          (allData.impact_statement_2 && allData.impact_statement_2.trim()) || 
                          null, // Also send camelCase
        is_active: allData.isActive !== undefined ? allData.isActive : true,
        isActive: allData.isActive !== undefined ? allData.isActive : true, // Send both for compatibility
      };
      
      // Location fields (always include, even if empty)
      beneficiaryData.city = allData.city || '';
      beneficiaryData.state = allData.state || '';
      beneficiaryData.zip_code = allData.zipCode || '';
      beneficiaryData.location = allData.location || (allData.city && allData.state ? `${allData.city}, ${allData.state}${allData.zipCode ? ' ' + allData.zipCode : ''}` : '');
      
      // Contact fields (always include, even if empty - these are important fields)
      beneficiaryData.phone = allData.phoneNumber || '';
      beneficiaryData.contact_name = allData.primaryContact || '';
      // Try sending email - backend may have added this column
      beneficiaryData.email = allData.primaryEmail || '';
      beneficiaryData.primary_email = allData.primaryEmail || ''; // Also try primary_email
      
      // Trust & Transparency fields (always include, even if empty)
      beneficiaryData.ein = allData.ein || '';
      beneficiaryData.website = allData.website || '';
      
      // Images (use from allData first, then fallback to state)
      const finalMainImageUrl = allData.mainImageUrl || mainImageUrl;
      const finalLogoUrl = allData.logoUrl || logoUrl;
      
      if (finalMainImageUrl) {
        beneficiaryData.imageUrl = finalMainImageUrl; // Backend expects camelCase
        beneficiaryData.main_image = finalMainImageUrl;
        beneficiaryData.main_image_url = finalMainImageUrl;
      }
      if (finalLogoUrl) {
        beneficiaryData.logoUrl = finalLogoUrl; // Backend expects camelCase
        beneficiaryData.logo = finalLogoUrl;
        beneficiaryData.logo_url = finalLogoUrl;
      }
      
      // Impact Metrics - Now accept full sentences (text)
      // Only include if they have values (non-empty strings)
      if (allData.livesImpacted && allData.livesImpacted.trim()) {
        beneficiaryData.livesImpacted = allData.livesImpacted.trim();
        beneficiaryData.lives_impacted = allData.livesImpacted.trim();
      }
      if (allData.programsActive && allData.programsActive.trim()) {
        beneficiaryData.programsActive = allData.programsActive.trim();
        beneficiaryData.programs_active = allData.programsActive.trim();
      }
      if (allData.directToProgramsPercentage && allData.directToProgramsPercentage.trim()) {
        beneficiaryData.directToProgramsPercentage = allData.directToProgramsPercentage.trim();
        beneficiaryData.direct_to_programs_percentage = allData.directToProgramsPercentage.trim();
      }
      
      // NOTE: The following fields are NOT included as they may not exist in backend:
      // - verification_status (doesn't exist - confirmed)
      // - likes, mutual, social (may not exist)
      // - additional_images (may not exist)
      // - profile_links (may not exist)
      // - latitude, longitude (can be null, but may cause issues)
      
      // Explicitly remove fields that don't exist in backend schema
      // This is a defensive cleanup to ensure no problematic fields slip through
      const fieldsToRemove = [
        // Email fields - REMOVED: Now trying to send email to backend
        // 'email', // Now trying to send this
        // 'primaryEmail', // This is the form field name, not backend field
        // Verification status (doesn't exist - confirmed)
        'verification_status',
        'verificationStatus',
        // Old impact metrics fields (replaced by new ones)
        'communities_served',
        'families_helped',
        'direct_to_programs',
        'communitiesServed',
        'familiesHelped',
        'directToPrograms',
        // Impact statements - REMOVED from removal list (backend now supports them)
        // 'impact_statement_1', // Now supported
        // 'impact_statement_2', // Now supported
        // 'impactStatement1', // Now supported
        // 'impactStatement2', // Now supported
        // Other potentially problematic fields
        'transparency_rating',
        'likes', // May not exist
        'mutual', // May not exist
        'social', // May not exist
        'additional_images', // May not exist
        'profile_links', // May not exist
        'latitude', // May cause issues if null
        'longitude', // May cause issues if null
      ];
      
      fieldsToRemove.forEach(field => {
        if (beneficiaryData.hasOwnProperty(field)) {
          delete beneficiaryData[field];
        }
      });
      
      // Final verification
      const hasRemovedFields = fieldsToRemove.some(field => beneficiaryData.hasOwnProperty(field));
      if (hasRemovedFields) {
        console.error('❌ CRITICAL: Some removed fields are still in payload!', fieldsToRemove.filter(f => beneficiaryData.hasOwnProperty(f)));
      }
      
      // Logging for debugging
      
      // CRITICAL: Verify Impact & Story fields are in payload
      
      
      // Verify critical fields are removed
      const criticalRemovedFields = ['verification_status', 'communities_served', 'email', 'likes', 'mutual'];
      criticalRemovedFields.forEach(field => {
        const isRemoved = !beneficiaryData.hasOwnProperty(field);
        if (!isRemoved) {
          console.error(`❌ CRITICAL: ${field} is still in payload!`);
        }
      });
      
      
      // Call API
      const response = await beneficiaryAPI.createBeneficiary(beneficiaryData);
      
      // Handle different response formats
      // Backend might return: { success: true, data: {...} } OR just the data directly
      const responseData = response.data || response;
      const isSuccess = response.success !== false; // Default to true if not specified
      
      
      if (responseData) {
        const beneficiaryId = responseData.id || responseData;
        if (typeof responseData === 'object') {
        }
      }
      
      if (isSuccess) {
        message.success('Beneficiary created successfully!');
        addNotification({
          title: 'Beneficiary invited',
          message: beneficiaryData.name || 'Beneficiary created',
          level: 'success',
        });
        // Call onSubmit callback (which should refresh the beneficiaries list)
        onSubmit(allData);
        handleCancel();
        // Note: The parent component (Beneficiaries) should refresh the list
        // in its onSubmit handler to show the newly created beneficiary
      } else {
        const errorMsg = response.error || responseData?.error || 'Failed to create beneficiary';
        console.error('❌ Backend error:', errorMsg);
        message.error(`Failed to create beneficiary: ${errorMsg}`);
        addNotification({
          title: 'Beneficiary invite failed',
          message: errorMsg,
          level: 'error',
        });
      }
      
    } catch (error: any) {
      console.error('❌ Error creating beneficiary:', error);
      const errorMsg = error?.message || 'Failed to create beneficiary. Please try again.';
      message.error(errorMsg);
      addNotification({
        title: 'Beneficiary invite failed',
        message: errorMsg,
        level: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Single-step submit. Every field is mounted at once now, so one
  // validateFields() covers the form. The state buckets are still spread in
  // because image uploads land in uploadImages via their own handlers, not
  // through the form.
  const handleSubmitAll = async () => {
    try {
      const values = await form.validateFields();
      await handleSubmit({
        ...basicDetails,
        ...trustTransparency,
        ...uploadImages,
        ...values,
      });
    } catch (err) {
      // antd surfaces per-field validation messages itself; nothing to add.
      console.warn('Beneficiary form validation failed:', err);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setBasicDetails({});
    setTrustTransparency({});
    setUploadImages({});
    // Reset image states
    setMainImageUrl(null);
    setLogoUrl(null);
    setAdditionalImages([]);
    setForm990File(null);
    setProfileLinks([{ channel: '', username: '' }]);
    onCancel();
  };

  // Takes an explicit step so the single-page form can render several
  // sections in one pass. The switch bodies are unchanged.
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div className="step-content">
            <Title level={4}>Basic Information</Title>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  name="beneficiaryName"
                  label="Beneficiary Name"
                  rules={[{ required: true, message: 'Please enter beneficiary name' }]}
                >
                  <Input placeholder="Enter organization name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="category"
                  label="Category"
                  rules={[{ required: true, message: 'Please select category' }]}
                >
                  <Select placeholder="Select category" showSearch optionFilterProp="children">
                    {BENEFICIARY_CATEGORIES.map((category) => (
                      <Option key={category.value} value={category.value} title={category.description}>
                        {category.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  name="type"
                  label="Type"
                  rules={[{ required: true, message: 'Please select type' }]}
                >
                  <Select placeholder="Select type">
                    <Option value="Large">Large</Option>
                    <Option value="Medium">Medium</Option>
                    <Option value="Small">Small</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  name="location"
                  label="Location"
                  rules={[{ required: true, message: 'Please enter location (City, State)' }]}
                  tooltip="Enter location as 'City, State' format (e.g., 'Atlanta, GA')"
                >
                  <Input placeholder="Atlanta, GA" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 16]}>
              <Col span={8}>
                <Form.Item
                  name="city"
                  label="City"
                  rules={[{ required: true, message: 'Please enter city' }]}
                >
                  <Input placeholder="Enter city" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="state"
                  label="State"
                  rules={[{ required: true, message: 'Please enter state' }]}
                >
                  <Input placeholder="Enter state" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="zipCode"
                  label="ZIP Code"
                  rules={[{ required: false }]}
                >
                  <Input placeholder="Enter ZIP code" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  name="primaryContact"
                  label="Primary Contact"
                  rules={[{ required: true, message: 'Please enter primary contact' }]}
                >
                  <Input placeholder="Enter primary contact name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="primaryEmail"
                  label="Primary Contact Email"
                  rules={[{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Please enter a valid email' }]}
                >
                  <Input placeholder="Enter email address" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  name="phoneNumber"
                  label="Phone Number"
                  rules={[{ required: false }]}
                >
                  <Input placeholder="Enter phone number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="website"
                  label="Website"
                  rules={[{ required: false }]}
                >
                  <Input placeholder="https://..." />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  name="social"
                  label="Social Media"
                  rules={[{ max: 100, message: 'Social media handle must be 100 characters or less' }]}
                  tooltip="Social media handle (e.g., @organizationname)"
                >
                  <Input placeholder="@organizationname" maxLength={100} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="isActive"
                  label="Active Status"
                  valuePropName="checked"
                  initialValue={true}
                  style={{ marginTop: 30 }}
                >
                  <Checkbox>Active (show in app)</Checkbox>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  name="likes"
                  label="Likes"
                  initialValue={0}
                >
                  <InputNumber
                    placeholder="0"
                    style={{ width: '100%' }}
                    min={0}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="mutual"
                  label="Mutual Connections"
                  initialValue={0}
                >
                  <InputNumber
                    placeholder="0"
                    style={{ width: '100%' }}
                    min={0}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="about"
              label="About"
              rules={[
                { required: true, message: 'Please enter description' },
                { min: 200, message: 'Description must be at least 200 characters' }
              ]}
            >
              <TextArea 
                rows={6} 
                placeholder="Detailed description of the beneficiary organization and their mission..." 
                showCount={{ formatter: ({ count, maxLength }) => `${count}/${maxLength}` }}
                maxLength={1000}
              />
            </Form.Item>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <Title level={4}>Trust & Transparency</Title>
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  name="ein"
                  label="EIN Number"
                >
                  <Input placeholder="e.g., 12-3456789" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="website"
                  label="Website"
                >
                  <Input placeholder="https://example.org" />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item
              name="verificationStatus"
              label="Verification Status"
              valuePropName="checked"
            >
              <Checkbox>Verified 501(c)(3) Nonprofit</Checkbox>
            </Form.Item>

            <Divider>Additional Documents</Divider>
            
            <Form.Item
              name="form990"
              label="Upload Form-990"
            >
              <Upload
                beforeUpload={(file) => {
                  setForm990File(file);
                  form.setFieldsValue({ form990: file });
                  return false; // Prevent auto upload
                }}
                onRemove={() => {
                  setForm990File(null);
                  form.setFieldsValue({ form990: null });
                }}
                maxCount={1}
                accept=".pdf,.doc,.docx"
              >
                <Button icon={<UploadOutlined />} className="upload-btn">
                  Select Form-990 File
                </Button>
              </Upload>
              {form990File && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Selected: {form990File.name}
                </Text>
              )}
            </Form.Item>

            <Divider>Profile Links</Divider>
            
            <div className="profile-links-section">
              <Title level={5} style={{ marginBottom: 16 }}>Enter your profile links</Title>
              {profileLinks.map((link, index) => (
                <Row gutter={[16, 16]} key={index} style={{ marginBottom: 16, alignItems: 'flex-start' }}>
                  <Col span={10}>
                    <Form.Item
                      label={index === 0 ? 'Select Channel' : ''}
                      rules={index === 0 ? [{ required: false }] : []}
                    >
                      <Select
                        placeholder="Select channel"
                        style={{ width: '100%' }}
                        value={link.channel}
                        onChange={(value) => updateProfileLink(index, 'channel', value)}
                      >
                        <Option value="facebook">Facebook</Option>
                        <Option value="twitter">Twitter</Option>
                        <Option value="instagram">Instagram</Option>
                        <Option value="linkedin">LinkedIn</Option>
                        <Option value="website">Website</Option>
                        <Option value="other">Other</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      label={index === 0 ? 'Enter Username/URL' : ''}
                      rules={index === 0 ? [{ required: false }] : []}
                    >
                      <Input
                        placeholder="Enter username or URL"
                        value={link.username}
                        onChange={(e) => updateProfileLink(index, 'username', e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4} style={{ paddingTop: index === 0 ? 30 : 0 }}>
                    {profileLinks.length > 1 && (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeProfileLink(index)}
                        style={{ marginTop: index === 0 ? 0 : 0 }}
                      >
                        Remove
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
              <Button
                className="add-profile-link-btn"
                icon={<PlusOutlined />}
                onClick={addProfileLink}
              >
                Add New Profile Link
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <Title level={4}>Upload Images</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
              Upload images to showcase your beneficiary organization. All images are optional but recommended.
            </Text>
            
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Card title="Main Image" style={{ marginBottom: '16px' }}>
                  <Form.Item
                    name="mainImage"
                    label="Upload Main Image"
                    rules={[{ required: false }]}
                  >
                    <ImageUpload
                      currentImageUrl={mainImageUrl || undefined}
                      onImageChange={handleMainImageChange}
                      title="Upload Beneficiary Main Image"
                      description="Main image displayed on beneficiary profile. Recommended: 1080px × 1080px. Max 5MB"
                    />
                  </Form.Item>
                </Card>
              </Col>
              
              <Col span={24}>
                <Card title="Organization Logo" style={{ marginBottom: '16px' }}>
                  <Form.Item
                    name="logo"
                    label="Upload Logo"
                    rules={[{ required: false }]}
                  >
                    <ImageUpload
                      currentImageUrl={logoUrl || undefined}
                      onImageChange={handleLogoChange}
                      title="Upload Organization Logo"
                      description="Logo displayed in beneficiary listings. Recommended: 1080px × 1080px. Max 5MB"
                    />
                  </Form.Item>
                </Card>
              </Col>
              
              <Col span={24}>
                <Card title="Additional Images">
                  <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                    Upload up to 3 additional images showcasing your programs and impact
                  </Text>
                  
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Text strong style={{ display: 'block', marginBottom: '8px' }}>Image 1</Text>
                      <ImageUpload
                        currentImageUrl={additionalImages[0] || undefined}
                        onImageChange={(url) => handleAdditionalImageChange(url, 0)}
                        title="Upload Image"
                        description="Click or drag"
                      />
                    </Col>
                    <Col span={8}>
                      <Text strong style={{ display: 'block', marginBottom: '8px' }}>Image 2</Text>
                      <ImageUpload
                        currentImageUrl={additionalImages[1] || undefined}
                        onImageChange={(url) => handleAdditionalImageChange(url, 1)}
                        title="Upload Image"
                        description="Click or drag"
                      />
                    </Col>
                    <Col span={8}>
                      <Text strong style={{ display: 'block', marginBottom: '8px' }}>Image 3</Text>
                      <ImageUpload
                        currentImageUrl={additionalImages[2] || undefined}
                        onImageChange={(url) => handleAdditionalImageChange(url, 2)}
                        title="Upload Image"
                        description="Click or drag"
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <div className="modal-header">
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            className="back-btn"
          />
          <div className="header-content">
            <Title level={3} className="modal-title">Invite Beneficiaries</Title>
            <Text className="modal-subtitle">Complete your details and send invite to beneficiaries</Text>
          </div>
        </div>
      }
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={1000}
      className="invite-beneficiary-modal"
    >
      {/* Single-page form. The four-step wizard went away with the Impact &
          Story fields — what remains (basics, trust, images) is short enough
          to read at once, and a wizard for three short groups was more
          ceremony than the task needs. Step 1 (Impact & Story) is simply not
          rendered. */}
      <div className="modal-content modal-content--single-step">
        <div className="form-content">
          <Form
            form={form}
            layout="vertical"
            requiredMark="optional"
            className="beneficiary-form"
          >
            {renderStepContent(0)}
            {renderStepContent(2)}
            {renderStepContent(3)}
            <div className="form-actions">
              <Button
                type="primary"
                onClick={handleSubmitAll}
                className="next-btn"
                loading={saving}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Add Beneficiary'}
                {!saving && <ArrowRightOutlined />}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default InviteBeneficiaryModal; 